import React, { useEffect, useRef } from 'react';
import { Form, Select, Spin } from 'antd';
import type { NamePath } from 'rc-field-form/lib/interface';
import { lookupNpi } from 'api/insuranceBilling';
import type { NpiLookupTaxonomy, NpiLookupVo, TNpiType } from 'types/insuranceBilling';
import { npiIssue, npiIssueText } from 'utils/npiCheck';
import HiddenFormField from '../HiddenFormField';
import TaxonomyPicker from './TaxonomyPicker';
import s from './s.module.less';

const CheckIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#16a34a"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <path d="M20 6 9 17l-5-5" />
    </svg>
);

type Props = {
    // 该 NPI 所在槽位的命名空间，如 ['billing'] / ['rendering','providers',i]；
    // 查询结果写回 [...ns,'npiType'] / [...ns,'npiRegistryName'] / [...ns,'taxonomyOptions'] / [...ns,'taxonomy']
    ns: (string | number)[];
    // NPI 在 ns 下的字段名。默认 'npi'；group credentialing 的 practice 表单叫 groupNpi
    npiKey?: string;
    // 该场景要求的 NPI 类型，查到的类型不符时报错并阻断 Continue：
    // rendering provider / credentialing 基本信息要 Individual（Type 1），practice 要 Organization（Type 2）
    expectType?: TNpiType;
    // 类型不符时的自定义文案（默认按 expectType 给通用文案）
    typeErrorText?: string;
    // 姓名核对：传表单里姓名字段的路径而非字符串，组件 useWatch 这些路径，
    // 用户查完 NPI 再改名字时红字会实时更新。接口没返回登记姓名（机构 NPI）时整项跳过
    nameFields?: { firstName?: NamePath; lastName?: NamePath };
    // 是否渲染 Taxonomy 下拉。credentialing 没有 taxonomy 字段，传 false
    showTaxonomy?: boolean;
    // Taxonomy 下拉末项 My taxonomy is not listed here（手填 code，标记写 [...ns,'taxManual']）。
    // 只有 insurance billing 的 billing / rendering provider 开启，credentialing 等其它宿主不受影响
    allowManualTaxonomy?: boolean;
    // 结果框外层容器的类名，只在有内容时才套一层 div（宿主是 flex column + gap 的场景不用传，
    // 间距由容器给；group practice 那种普通块级流需要靠它补下间距）
    className?: string;
    // 查询成功（类型已确认且非报错态）时渲染在结果框下方的说明文字
    typeHint?: (npiType: TNpiType) => React.ReactNode;
    // NPI 查询通道。默认走 insurance-billing onboarding 那条（经 AskChart，校验 owner + api key）；
    // 未开通 Insurance Billing 的场景传 api/common 的 lookupNpiStandalone（后端 §3.5 独立端点，
    // 任何登录 provider 可调）。两条出参结构一致，组件不关心用的是哪条。只接模块级函数引用——
    // 内联箭头函数会被下面 effect 的闭包捕获，虽不进依赖数组，但每次 render 换引用没有意义
    lookup?: typeof lookupNpi;
};

// nameFields 没传时给 useWatch 的占位路径：hooks 不能按条件调用，而这个字段永不存在，
// 订阅它等于不订阅（useWatch 对未注册字段返回 undefined 且不触发重渲染）
const NAME_WATCH_UNUSED: NamePath = ['__npiNameWatchUnused'];

const primaryTax = (taxonomies: NpiLookupTaxonomy[]) => taxonomies.find((t) => t.primary) || taxonomies[0];

// NPI Registry 查询结果框（原型 registryBox，Step 1 + Step 5 共用）：
// 10 位有效 NPI 触发查询 → 显示 NPI-1/NPI-2 类型 + Taxonomy 下拉（默认 primary）；
// 非空但不足 10 位、查不到均显示红色错误；rendering 且为 Organization 显示红色阻断错误。
// 查询进行中（含防抖等待）置 npiChecking 辅助字段，供 Continue 硬拦截 / Review 校验禁点
export default function NpiRegistryBox({
    ns,
    npiKey = 'npi',
    expectType,
    typeErrorText,
    nameFields,
    showTaxonomy = true,
    allowManualTaxonomy = false,
    className,
    typeHint,
    lookup = lookupNpi,
}: Props) {
    const form = Form.useFormInstance();
    const npi = Form.useWatch([...ns, npiKey], form);
    const npiType = Form.useWatch([...ns, 'npiType'], form);
    const npiChecking = Form.useWatch([...ns, 'npiChecking'], form);
    const npiRegistryName = Form.useWatch([...ns, 'npiRegistryName'], form);
    const taxonomyOptions: NpiLookupTaxonomy[] = Form.useWatch([...ns, 'taxonomyOptions'], form) || [];
    // 姓名比对的两个来源字段：用户查完 NPI 之后再改名字，红字要跟着变
    const expectedFirstName = Form.useWatch(nameFields?.firstName || NAME_WATCH_UNUSED, form);
    const expectedLastName = Form.useWatch(nameFields?.lastName || NAME_WATCH_UNUSED, form);
    // 防抖 + 竞态保护：只应用最后一次输入的查询结果
    const latestNpiRef = useRef<string>('');
    // 是否已在本次挂载中见过有效 NPI：切 step 返回时表单里已有查询结果，
    // 首个有效 NPI 不重复查询（避免结果框消失-重现的闪烁 + 冗余请求）
    const seenValidNpiRef = useRef(false);

    useEffect(() => {
        const digits = String(npi || '').replace(/\D/g, '');
        latestNpiRef.current = digits;
        if (!/^\d{10}$/.test(digits)) {
            // NPI 不完整：清掉查询结果（旧结果对应上一个输入值，已失效）
            if (npiType) {
                form.setFieldValue([...ns, 'npiType'], undefined);
                form.setFieldValue([...ns, 'npiRegistryName'], undefined);
                form.setFieldValue([...ns, 'taxonomyOptions'], undefined);
                form.setFieldValue([...ns, 'taxonomy'], undefined);
                if (allowManualTaxonomy) form.setFieldValue([...ns, 'taxManual'], false);
            }
            // 不满 10 位不存在进行中的查询
            if (form.getFieldValue([...ns, 'npiChecking'])) {
                form.setFieldValue([...ns, 'npiChecking'], false);
            }
            return undefined;
        }
        const firstValid = !seenValidNpiRef.current;
        seenValidNpiRef.current = true;
        // npiChecking 为 true 说明上次查询中途被打断（如切步骤卸载清掉了 timer），不跳过、重新查询，
        // 否则 checking 卡死会永久禁用 Continue
        if (
            firstValid &&
            !form.getFieldValue([...ns, 'npiChecking']) &&
            form.getFieldValue([...ns, 'npiType']) &&
            (form.getFieldValue([...ns, 'taxonomyOptions']) || []).length > 0
        ) {
            return undefined;
        }
        // 挂载后的首次查询只是补回下拉选项（hydrate 只回 code）：已存的手填 code 保留；
        // 之后 NPI 一变就是新的 NPPES 记录，手填作废、回到默认 primary（原型同口径）
        const keepManual = firstValid && allowManualTaxonomy && !!form.getFieldValue([...ns, 'taxManual']);
        // Continue 从防抖计时开始就要禁用：先置 checking，结果应用时清除
        form.setFieldValue([...ns, 'npiChecking'], true);
        const timer = setTimeout(async () => {
            // 直连 NPPES 查询接口；约定查不到返回 400 → notfound（disableErrorNote，不弹全局错误）
            let rec: NpiLookupVo | null = null;
            try {
                const res: any = await lookup(digits);
                if (!res?.error) rec = res?.data?.data ?? null;
            } catch (e) {
                rec = null;
            }
            // 竞态丢弃的旧结果不清 checking：更新一轮查询已重新置位，由它负责清除
            if (latestNpiRef.current !== digits) return;
            form.setFieldValue([...ns, 'npiChecking'], false);
            if (!rec) {
                form.setFieldValue([...ns, 'npiType'], 'notfound');
                form.setFieldValue([...ns, 'npiRegistryName'], undefined);
                form.setFieldValue([...ns, 'taxonomyOptions'], []);
                form.setFieldValue([...ns, 'taxonomy'], undefined);
                return;
            }
            // npiType 缺失时从 NPPES 原始枚举兜底推导
            form.setFieldValue(
                [...ns, 'npiType'],
                rec.npiType || (rec.enumerationType === 'NPI-2' ? 'Type 2' : 'Type 1'),
            );
            // 登记姓名留一份在表单里，姓名比对才能是对表单值的纯函数（Review 校验与内联红字共用口径）；
            // 机构 NPI 这两项为空，比对会整项跳过
            form.setFieldValue([...ns, 'npiRegistryName'], { firstName: rec.firstName, lastName: rec.lastName });
            const taxonomies = rec.taxonomies || [];
            form.setFieldValue([...ns, 'taxonomyOptions'], taxonomies);
            if (keepManual) return;
            if (allowManualTaxonomy) form.setFieldValue([...ns, 'taxManual'], false);
            const current = form.getFieldValue([...ns, 'taxonomy']);
            const valid = taxonomies.some((t) => t.code === current);
            if (!valid) {
                // 默认选中后端算好的 primaryTaxonomy，前端 primary 标记兜底
                form.setFieldValue(
                    [...ns, 'taxonomy'],
                    rec.primaryTaxonomy?.code ?? (taxonomies.length ? primaryTax(taxonomies).code : undefined),
                );
            }
        }, 450);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [npi]);

    const typeCap = npiType === 'Type 2' ? 'Organization (NPI-2)' : 'Individual (NPI-1)';

    // 内联展示与 Continue / Review 拦截共用同一个纯函数（utils/npiCheck），避免两边口径漂移。
    // 用当前输入值直接算：清旧结果的 effect 在渲染之后才跑，此处不能只看上一轮的 npiType
    const issue = npiIssue(
        { [npiKey]: npi, npiType, npiChecking, npiRegistryName },
        {
            npiKey,
            expectType,
            expectedName: nameFields ? { firstName: expectedFirstName, lastName: expectedLastName } : undefined,
        },
    );

    let content: React.ReactNode = null;
    if (issue === 'checking') {
        // 查询期间（含防抖等待）显示 loading，此时 Continue 被禁用，给用户明确反馈
        content = (
            <div className={s.registryLoading}>
                <Spin size="small" />
                <span>Checking the NPI Registry…</span>
            </div>
        );
    } else if (issue && issue !== 'unchecked') {
        // unchecked（本次挂载还没查过）不展示任何状态，等查询结果落定再说
        content = (
            <div className={s.registryError}>
                {issue === 'wrongType' && typeErrorText ? typeErrorText : npiIssueText(issue, expectType)}
            </div>
        );
    } else if (npiType && npiType !== 'notfound') {
        const hint = typeHint?.(npiType);
        content = (
            <>
                <div className={s.registry}>
                    <div className={s.registryHead}>
                        <CheckIcon />
                        <span>
                            Found in NPI Registry · <strong>{typeCap}</strong>
                        </span>
                    </div>
                    {showTaxonomy && (taxonomyOptions.length > 0 || allowManualTaxonomy) && (
                        <div className={s.registryTax}>
                            <Form.Item name={[...ns, 'taxonomy']} label="Taxonomy">
                                {allowManualTaxonomy ? (
                                    <TaxonomyPicker options={taxonomyOptions} manualPath={[...ns, 'taxManual']} />
                                ) : (
                                    <Select
                                        size="large"
                                        options={taxonomyOptions.map((t) => ({
                                            value: t.code,
                                            label: `${t.code} · ${t.desc}${t.primary ? ' (primary)' : ''}`,
                                        }))}
                                    />
                                )}
                            </Form.Item>
                        </div>
                    )}
                </div>
                {hint && <div className={s.registryHint}>{hint}</div>}
            </>
        );
    }

    return (
        <>
            {/* 注册辅助字段：useWatch 只对已注册字段生效，必须无条件渲染（不能随结果框一起卸载） */}
            <Form.Item name={[...ns, 'npiType']} noStyle>
                <HiddenFormField />
            </Form.Item>
            <Form.Item name={[...ns, 'npiRegistryName']} noStyle>
                <HiddenFormField />
            </Form.Item>
            <Form.Item name={[...ns, 'taxonomyOptions']} noStyle>
                <HiddenFormField />
            </Form.Item>
            <Form.Item name={[...ns, 'npiChecking']} noStyle>
                <HiddenFormField />
            </Form.Item>
            {allowManualTaxonomy && (
                <Form.Item name={[...ns, 'taxManual']} noStyle>
                    <HiddenFormField />
                </Form.Item>
            )}
            {/* 没有可见内容时不渲染容器：空 div 的外边距会在字段之间留下莫名的空隙 */}
            {className && content ? <div className={className}>{content}</div> : content}
        </>
    );
}
